import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, ScanSearch } from "lucide-react";
import { toast } from "sonner";

export default function Auth({ mode }) {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const redirect = sp.get("redirect") || "/account";
  const { login, register, forgotPassword, resetPassword } = useAuth();
  const [f, setF] = useState({ name: "", email: "", password: "", token: sp.get("token") || "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);
  const upd = (k, v) => setF((x) => ({ ...x, [k]: v }));

  const titles = {
    login: "Welcome back", register: "Create your account",
    forgot: "Reset your password", reset: "Set a new password",
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      if (mode === "login") { await login(f.email, f.password); nav(redirect); }
      else if (mode === "register") { await register(f.email, f.password, f.name); nav(redirect); }
      else if (mode === "forgot") { await forgotPassword(f.email); setDone(true); }
      else if (mode === "reset") { await resetPassword(f.token, f.password); toast.success("Password updated"); nav("/login"); }
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] grid place-items-center px-5 py-10 bg-slate-50">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2 mb-6">
          <span className="grid place-items-center w-9 h-9 rounded-lg bg-slate-900 text-white"><ScanSearch className="w-5 h-5" /></span>
          <span className="font-heading font-bold text-lg text-slate-900">Opportunity<span className="text-indigo-600">Scanner</span></span>
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-7">
          <h1 className="font-heading text-2xl font-bold text-slate-900">{titles[mode]}</h1>
          {mode === "forgot" && done ? (
            <p className="mt-4 text-sm text-slate-600" data-testid="forgot-sent">
              If an account exists for that email, a password-reset link has been sent. Check your inbox.
            </p>
          ) : (
            <form onSubmit={submit} className="mt-5 space-y-3" data-testid={`auth-form-${mode}`}>
              {mode === "register" && (
                <Input data-testid="auth-name" placeholder="Your name" value={f.name}
                  onChange={(e) => upd("name", e.target.value)} required />
              )}
              {mode === "reset" && (
                <Input data-testid="auth-token" placeholder="Reset token" value={f.token}
                  onChange={(e) => upd("token", e.target.value)} required />
              )}
              {mode !== "reset" && (
                <Input data-testid="auth-email" type="email" placeholder="you@business.com" value={f.email}
                  onChange={(e) => upd("email", e.target.value)} required />
              )}
              {mode !== "forgot" && (
                <Input data-testid="auth-password" type="password"
                  placeholder={mode === "reset" ? "New password" : "Password"} value={f.password}
                  onChange={(e) => upd("password", e.target.value)} required minLength={8} />
              )}
              {err && <p className="text-sm text-red-600" data-testid="auth-error">{err}</p>}
              <Button type="submit" disabled={busy} data-testid="auth-submit"
                className="w-full bg-slate-900 hover:bg-slate-800">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> :
                  mode === "login" ? "Sign in" : mode === "register" ? "Create account" :
                    mode === "forgot" ? "Send reset link" : "Update password"}
              </Button>
            </form>
          )}
          <div className="mt-5 text-sm text-slate-500 space-y-1">
            {mode === "login" && (<>
              <p>New here? <Link to="/register" className="text-indigo-600 font-medium" data-testid="link-register">Create an account</Link></p>
              <p><Link to="/forgot-password" className="text-indigo-600" data-testid="link-forgot">Forgot password?</Link></p>
            </>)}
            {mode === "register" && <p>Already have an account? <Link to="/login" className="text-indigo-600 font-medium" data-testid="link-login">Sign in</Link></p>}
            {(mode === "forgot" || mode === "reset") && <p><Link to="/login" className="text-indigo-600">Back to sign in</Link></p>}
          </div>
        </div>
      </div>
    </div>
  );
}
